import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import LookupSelect from '../components/LookupSelect';
import { lookupFacility } from '../lookups';

/**
 * Add a locker — the door system a facility's codes get installed into.
 *
 * **The type comes first and decides the rest of the form.** `getPropertiesByTypeId`
 * answers the settings that type has, and those are the only ones the backend
 * reads; picking a different type replaces them. The old page modelled that by
 * rendering the type select alone until something was chosen, and this one keeps
 * it — a form whose fields you cannot know yet is worse than one field.
 *
 * The payload is the old page's, on purpose. `addLocker` reads `locker.facility.value`
 * for the facility id, and puts every other key through as it is, so the type goes
 * in as `LockerType` and each property under its own name with the capital it was
 * given (`RandomCodeLenght`, the backend's misspelling included). Renaming any of
 * that here would be a backend conversation, not a rewrite.
 *
 * What is new is only what the old form left to the person filling it in: it let
 * you save with no facility and no name, which creates a locker that belongs to
 * nothing. Save stays disabled until the form can produce a locker somebody can
 * find again.
 */

const label = name => (name.toLowerCase() === 'randomcodelenght' ? 'Code length' : name);

const LockerCreateInternal = ({ api }) => {
  const navigate = useNavigate();

  const [types, setTypes] = useState(null);
  const [typeId, setTypeId] = useState('');
  const [properties, setProperties] = useState([]);
  const [name, setName] = useState('');
  const [facility, setFacility] = useState({ value: '', label: '' });
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    let alive = true;
    api.lockers.getLockerTypes()
      .then(answer => {
        if (!alive) return;
        const rows = Array.isArray(answer?.data) ? answer.data : [];
        // `{ key, value }` from the backend; the key is what `LockerType` takes.
        setTypes(rows.map(row => ({ value: String(row.key), label: row.value })));
      })
      .catch(() => { if (alive) setTypes([]); });
    return () => { alive = false; };
  }, [api]);

  const chooseType = useCallback(async next => {
    setTypeId(next);
    setValues({});
    setProperties([]);
    if (!next || next === '0') return;

    try {
      const answer = await api.lockers.getPropertiesByTypeId(next);
      setProperties(Array.isArray(answer?.data) ? answer.data : []);
    } catch (error) {
      // Without the list there is nothing type-specific to fill in, which is
      // safer than guessing which keys this type wants.
      setProperties([]);
      setNote({ tone: 'error', text: 'Could not read the settings for that type. The locker can still be created with a name and a facility.' });
    }
  }, [api]);

  const chosen = types && types.find(type => type.value === typeId);
  const missingProperties = properties.filter(property => !String(values[property] ?? '').trim());
  const canSave = !!typeId && typeId !== '0' && !!name.trim() && !!facility.value
    && missingProperties.length === 0;

  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      // Exactly the shape `addLocker` expects: it reads `facility.value` itself
      // and forwards everything else untouched.
      const payload = { name: name.trim(), facility, LockerType: typeId, ...values };
      const response = await api.lockers.addLocker(payload);

      if (response?.success === false) {
        setNote({ tone: 'error', text: `Unable to add the locker. ${response.message || ''}`.trim() });
        return;
      }
      if (!response?.id) {
        setNote({ tone: 'error', text: 'The locker was not created — the server answered without an id.' });
        return;
      }
      navigate(`/next/locker/${response.id}`);
    } catch (error) {
      setNote({ tone: 'error', text: error?.message || 'Unable to add the locker.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="next-page">
      <BackToList to="/next/lockers">← All lockers</BackToList>

      <header className="next-page__header">
        <h1>New locker</h1>
        <p className="next-muted">
          A door system at one facility. Its type decides what else it needs.
        </p>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <section className="next-card">
        <div className="next-card__head"><h2>Type</h2></div>

        <div className="next-form__grid">
          <label className="next-filter">
            <span className="next-filter__label">Locker type</span>
            <select
              className="next-input"
              value={typeId}
              disabled={!types}
              onChange={event => chooseType(event.target.value)}
            >
              <option value="">{types ? 'Choose a type…' : 'Loading…'}</option>
              {(types || []).map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <span className="next-form__hint">
              Choosing a type loads the settings that type has. Changing it clears them.
            </span>
          </label>
        </div>
      </section>

      {/* Nothing below is answerable until a type is picked, so nothing below is
          shown. The old page did the same, and it is the honest shape. */}
      {chosen && (
        <>
          <section className="next-card">
            <div className="next-card__head">
              <h2>Locker</h2>
              <span className="next-muted">{chosen.label}</span>
            </div>

            <div className="next-form__grid">
              <label className="next-filter">
                <span className="next-filter__label">Name</span>
                <input
                  className="next-input"
                  value={name}
                  placeholder="What this door is called"
                  onChange={event => setName(event.target.value)}
                />
              </label>

              <LookupSelect
                label="Facility"
                value={facility.value}
                placeholder="Type a facility name or paste an id"
                loadOptions={key => lookupFacility(api, key)}
                onChange={value => setFacility(value ? { value, label: '' } : { value: '', label: '' })}
              />
            </div>
          </section>

          {properties.length > 0 && (
            <section className="next-card">
              <div className="next-card__head">
                <h2>Settings</h2>
                <span className="next-muted">
                  {properties.length} for a {chosen.label.toLowerCase()} locker
                </span>
              </div>

              <div className="next-form__grid">
                {properties.map(property => (
                  <label className="next-filter" key={property}>
                    <span className="next-filter__label">{label(property)}</span>
                    <input
                      className="next-input"
                      value={values[property] ?? ''}
                      placeholder={property}
                      onChange={event => setValues({ ...values, [property]: event.target.value })}
                    />
                  </label>
                ))}
              </div>
            </section>
          )}

          <div className="next-form__actions">
            <button
              type="button"
              className="next-btn next-btn--primary"
              disabled={!canSave || busy}
              onClick={save}
            >
              {busy ? 'Creating…' : 'Create locker'}
            </button>
            <Link className="next-btn" to="/next/lockers">Cancel</Link>

            {/* Says which field is holding it up, rather than leaving a disabled
                button with no explanation. */}
            {!canSave && (
              <span className="next-form__hint">
                {!name.trim() && 'A name is needed. '}
                {!facility.value && 'A facility is needed. '}
                {missingProperties.length > 0 && `${missingProperties.map(label).join(', ')} ${missingProperties.length === 1 ? 'is' : 'are'} needed.`}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

LockerCreateInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const LockerCreate = withApi(LockerCreateInternal);

LockerCreate.requiresLogin = true;

export default LockerCreate;
